<script setup lang="tsx">
import { computed, onMounted, ref, watch } from 'vue'
import CancelSaveButtons from '../widgets/CancelSaveButtons.vue'
import sliderStore from '@/stores/slider'
import toastStore from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import type { Instance, User, Response, Responsible, ResponsibleFor, CustomFieldDefinition } from '@/models/api-generated'
import http from '@/services/http'
import { NoAudit } from '@/models/enums-generated'
import { refreshCallback } from './shared'
import InstanceSelect from '../widgets/InstanceSelect.vue'
import ReplaceResponsibleDialog from './ReplaceResponsibleDialog.vue'
import ConfirmDeleteDialog from './ConfirmDeleteDialog.vue'
import CustomValueEdit from '../custom_field/CustomValueEdit.vue'
import CustomValueDisplay from '../custom_field/CustomValueDisplay.vue'
import { fetchCustomValues, type CustomValueMap } from '@/services/custom'
import { roleRefreshCallback } from '../role/shared'
import RoleSelect from '../widgets/RoleSelect.vue'
import { viewing } from '@/services/utils'
import InstanceList from './InstanceList.vue'

const props = defineProps<{
	userId: number
}>()

const { t } = useI18n()
const slider = sliderStore()
const toaster = toastStore()
const customValues = ref<CustomValueMap>()
const custom = ref<CustomFieldDefinition[]>([])

const isAdd = computed(() => props.userId === 0)
const isEdit = ref(false)
const canEdit = computed(() => isAdd.value || isEdit.value)
const canEditInstance = computed(() => {
	return canEdit.value && sessionMap.user.id !== user.value.id
})
const showDeleteDialog = ref(false)
const showRespDialog = ref(false)

const user = ref<User>(defaultUser())
const emailActivate = ref(false)
const password2 = ref('')

const isValidInstance = computed(() => user.value.instances.length > 0)
const isValidFirstname = computed(() => user.value.firstname.length > 0)
const isValidLastname = computed(() => user.value.lastname.length > 0)
const isValidEmail = computed(() => user.value.email.length > 2 && user.value.email.includes('@')) // /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(user.value.email))
const isValidIdCode = computed(() => user.value.idCode.length === 11)
const isValidAdUser = computed(() => user.value.adUser.length > 0)
const isValidPassword = computed(
	() => (isEdit.value && user.value.password.length === 0) || (canEdit.value && user.value.password.length >= 12),
)
const isValidPassword2 = computed(() => isValidPassword.value && user.value.password === password2.value)

const isUniqueUser = computed(() => {
	return isValidEmail.value || isValidIdCode.value || isValidAdUser.value
})

const isCurrentUser = computed(() => sessionMap.user.id == user.value.id)

const canSave = computed(() => {
	return (
		isUniqueUser.value &&
		isValidInstance.value &&
		isValidFirstname.value &&
		isValidLastname.value &&
		(!emailActivate.value || (emailActivate.value && isValidEmail.value)) &&
		(isValidPassword2.value || emailActivate.value)
	)
})

const userAsResponsible = computed(() => {
	const responsible: Responsible = {
		id: user.value.id,
		type: 'users',
		trackBy: `users_${user.value.id}`,
		label: user.value.label,
	}
	return responsible
})

const respCount = ref(0)

const editBtns = (
	<CancelSaveButtons saveId="submit-user" cancelId="cancel-user" canSave={canSave} onCancel={cancel} onSave={save}></CancelSaveButtons>
)
const viewBtns = (
	<div class="d-flex gap-2">
		<button data-test="edit-user" class="btn btn-primary flex-fill" onClick={() => (isEdit.value = true)}>
			{t('misc.edit')}
		</button>
		<button data-test="delete-user" class="btn btn-danger flex-fill" onClick={() => (showDeleteDialog.value = true)}>
			{t('misc.delete')}
		</button>
	</div>
)

function defaultUser(): User {
	const instances: Instance[] = []
	if (viewing.value) {
		const inst = sessionMap.user.instances.find((x) => x.id === viewing.value)
		if (inst) instances.push(inst)
	}

	return {
		id: 0,
		email: '',
		idCode: '',
		adUser: '',
		password: '',
		firstname: '',
		lastname: '',
		instances,
		auditType: NoAudit,
		active: 1,
		customFields: {},
	} as User
}

async function getCustomFields() {
	if (!viewing.value) return
	const { data } = await http.get<Response<CustomFieldDefinition[]>>(`api/customFields/users`)
	if (data.data?.length) custom.value.push(...data.data)

	if (!isAdd.value) customValues.value = await fetchCustomValues('users', props.userId)
}

onMounted(async () => {
	await refresh()
	slider.watchChanges(user)
})

watch(() => props.userId, refresh)

watch(isEdit, (edit) => {
	slider.setFooter(edit ? editBtns : viewBtns)
})

async function refresh() {
	isEdit.value = false
	emailActivate.value = props.userId ? false : true
	password2.value = ''

	if (props.userId) {
		slider.setFooter(viewBtns)
		const { data } = await http.get<Response<User>>(`api/user/${props.userId}`)
		if (data.data) user.value = data.data
	} else {
		slider.setFooter(editBtns)
		user.value = defaultUser()
	}
	await getCustomFields()
	if (user.value && userAsResponsible.value) {
		await GetRespCount()
	}
}
// parem oleks kogu loogika viia serveri poolele, küsiks juba andmebaasist ainult count.  Aga praegu on nii, et välistada count ja tegelike objektide vahel erinevusi
async function GetRespCount() {
	const { data } = await http.get<Response<ResponsibleFor>>(
		`api/responsibleFor/${userAsResponsible.value.type}/${userAsResponsible.value.id}`,
	)

	respCount.value = 0
	if (data.data) {
		respCount.value += data.data.assets.length
		respCount.value += data.data.dataAssets.length
		respCount.value += data.data.methodMeta.length
		respCount.value += data.data.moduleMeta.length
		respCount.value += data.data.moduleAssets.length
		respCount.value += data.data.businessProcesses.length
		respCount.value += data.data.riskShort.length
	}
}

async function cancel() {
	const doCancel = async () => {
		if (isAdd.value) {
			slider.close()
		} else {
			await refresh()
		}
	}

	if (slider.getHasUnsavedChanges()) {
		slider.requestCancel(doCancel)
	} else {
		await doCancel()
	}
}

async function save() {
	let url = 'api/user'
	if (emailActivate.value) url += '?emailActivate=1'

	const { status } = await http.post(url, user.value)
	if (status === 200) {
		if (isAdd.value) toaster.showSuccess(t('misc.successfullyAdded'))
		else toaster.showSuccess(t('misc.successfullyEdited'))

		if (canEdit.value) {
			slider.setUnsavedChanges(false)
			slider.close()
			if (refreshCallback.value) refreshCallback.value()
		}
		roleRefreshCallback.value?.()
		if (user.value.id === sessionMap.user.id) window.location.reload()
	}
}

async function resendActivation() {
	const { status } = await http.post(`api/user/sendActivation/${user.value.id}`)
	if (status === 200) toaster.showSuccess(t('activationEmailSent'))
}
function showResp() {
	showRespDialog.value = true
}

async function confirmDelete(replacementResponsible?: Responsible) {
	await http.post(`api/user/delete/${user.value.id}`, replacementResponsible)
	if (refreshCallback.value) refreshCallback.value()
	showDeleteDialog.value = false
	toaster.showSuccess(t('misc.successfullyDeleted'))
	roleRefreshCallback.value?.()
}

async function confirmReplace(replacementResponsible?: Responsible) {
	await http.post(`api/user/changeResponsibilities/${user.value.id}`, replacementResponsible)
	if (refreshCallback.value) refreshCallback.value()
	showRespDialog.value = false
	await refresh()
	toaster.showSuccess(t('misc.successfullyResponsibilityChanged'))
	roleRefreshCallback.value?.()
}

function deleteDialogClosed(confirmed: boolean) {
	if (confirmed) {
		slider.setUnsavedChanges(false)
		slider.close()
	}
}

function updateCustomValue(fieldId: number, value: string | undefined) {
	if (!value) return
	customValues.value ??= {}
	customValues.value[fieldId] ??= {}
	customValues.value[fieldId][user.value.id] = value
	user.value.customFields ||= {}
	user.value.customFields[fieldId] ??= {}
	user.value.customFields[fieldId][user.value.id] = value
}
</script>

<template>
	<div :class="{ striped: !isEdit }">
		<div class="row">
			<label for="user-firstname" class="col-4 col-form-label">{{ $t('firstname') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<div v-if="canEdit" class="w-100">
					<input
						data-test="user-firstname"
						class="form-control w-100 mb-1"
						type="text"
						maxlength="100"
						v-model="user.firstname"
						:class="{ 'is-invalid': !isValidFirstname }"
					/>
					<div class="invalid-feedback">
						{{ $t('misc.validField') }}
					</div>
				</div>
				<div v-else>
					<span class="text-end">{{ user.firstname }}</span>
				</div>
			</div>
		</div>

		<div class="row">
			<label for="user-lastname" class="col-4 col-form-label">{{ $t('lastname') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<div v-if="canEdit" class="w-100">
					<input
						data-test="user-lastname"
						class="form-control w-100 mb-1"
						type="text"
						maxlength="100"
						v-model="user.lastname"
						:class="{ 'is-invalid': !isValidLastname }"
					/>
					<div class="invalid-feedback">
						{{ $t('misc.validField') }}
					</div>
				</div>
				<div v-else>
					<span class="text-end">{{ user.lastname }}</span>
				</div>
			</div>
		</div>

		<div class="row">
			<label for="user-email" class="col-4 col-form-label">{{ $t('email') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<div v-if="canEdit" class="w-100">
					<input
						data-test="user-email"
						class="form-control w-100 mb-1"
						type="email"
						maxlength="60"
						v-model="user.email"
						:class="{ 'is-invalid': !isValidEmail && !isUniqueUser }"
					/>
					<div class="invalid-feedback">
						{{ $t('user.validEmail', { symbol: '@' }) }}
					</div>
				</div>
				<div v-else>
					<span class="text-end">{{ user.email }}</span>
				</div>
			</div>
		</div>

		<div class="row">
			<label for="user-idcode" class="col-4 col-form-label">{{ $t('idCode') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<div v-if="canEdit" class="w-100">
					<input
						data-test="user-idcode"
						class="form-control w-100 mb-1"
						type="text"
						maxlength="11"
						v-model="user.idCode"
						:class="{ 'is-invalid': !isValidIdCode && !isUniqueUser }"
					/>
					<div class="invalid-feedback">
						{{ $t('user.validIdCode') }}
					</div>
				</div>
				<div v-else>
					<span class="text-end">{{ user.idCode }}</span>
				</div>
			</div>
		</div>

		<div class="row">
			<label for="user-aduser" class="col-4 col-form-label">{{ $t('adUser') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<div v-if="canEdit" class="w-100">
					<input
						data-test="user-aduser"
						class="form-control w-100 mb-1"
						type="text"
						maxlength="100"
						v-model="user.adUser"
						:class="{ 'is-invalid': !isValidAdUser && !isUniqueUser }"
					/>
					<div class="invalid-feedback">
						{{ $t('misc.validField') }}
					</div>
				</div>
				<div v-else>
					<span class="text-end">{{ user.adUser }}</span>
				</div>
			</div>
		</div>

		<div class="row">
			<label for="user-instance" class="col-4 col-form-label">{{ $t('instance') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<InstanceSelect
					v-if="canEditInstance"
					data-test="user-instance"
					:multiple="true"
					v-model="user.instances"
					:class="{ 'is-invalid': !isValidInstance }"
					class="mb-2 w-100"
				/>
				<div class="mb-2" v-else>
					<InstanceList :instances="user.instances" />
				</div>
			</div>
		</div>

		<template v-if="!canEdit && customValues">
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex align-items-center justify-content-end text-break">
						<CustomValueDisplay :field="field" :value="customValues?.[field.id]?.[user.id] ?? ''" />
					</div>
				</div>
			</template>
		</template>
		<template v-else>
			<template v-for="field in custom" :key="field.id">
				<div class="row mt-2 text-wrap">
					<div class="col">{{ field.label }}</div>
					<div class="col d-flex align-items-center justify-content-end text-break">
						<CustomValueEdit
							:field="field"
							:modelValue="customValues?.[field.id]?.[user.id] ?? ''"
							@update:modelValue="(val) => updateCustomValue(field.id, val)"
						/>
					</div>
				</div>
			</template>
		</template>

		<div class="row">
			<label for="user-roles" class="col-4 col-form-label">{{ $t('role', 2) }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<RoleSelect v-if="canEdit" data-test="user-roles" class="mb-2 w-100" v-model="user.roles" />
				<span v-else>{{ user.roles?.map((x) => x.name).join(', ') }}</span>
			</div>
		</div>

		<div class="row">
			<label for="user-resp" class="col-4 col-form-label">{{ $t('respObjects') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<div v-if="canEdit" class="w-100 d-flex align-items-center">
					<span class="text-start flex-grow-1">{{ respCount }}</span>

					<button data-test="change-resp-user" class="btn btn-primary" @click="showResp">
						{{ t('misc.giveAwayResponsibilities') }}
					</button>
				</div>
				<div v-else>
					<span class="text-end">{{ respCount }}</span>
				</div>
			</div>
		</div>

		<div v-if="(isAdd && !emailActivate) || !isAdd" class="row">
			<label for="user-active" class="col-4 col-form-label">{{ $t('active') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<div v-if="canEdit && !isCurrentUser" class="form-check form-switch">
					<input
						class="form-check-input"
						type="checkbox"
						role="switch"
						data-test="user-active"
						v-model="user.active"
						:true-value="1"
						:false-value="0"
					/>
				</div>
				<div v-else>
					<i v-if="user.active" class="bi bi-circle-fill status-normal-background"></i>
					<i v-else class="bi bi-circle-fill status-verybig-background"></i>
				</div>
			</div>
		</div>

		<div v-if="isAdd" class="row">
			<label for="user-activate" class="col-4 col-form-label">{{ $t('emailActivate') }}</label>
			<div class="col-8 d-flex align-items-center justify-content-end">
				<div v-if="canEdit" class="form-check form-switch">
					<input class="form-check-input" type="checkbox" role="switch" data-test="user-activate" v-model="emailActivate" />
				</div>
				<div v-else>
					<i v-if="user.active" class="bi bi-circle-fill status-normal-background"></i>
					<i v-else class="bi bi-circle-fill status-verybig-background"></i>
				</div>
			</div>
		</div>

		<template v-if="(isAdd && !emailActivate) || isEdit">
			<input name="disable-pwd-mgr-1" type="password" data-test="disable-pwd-mgr-1" style="display: none" value="disable-pwd-mgr-1" />
			<input name="disable-pwd-mgr-2" type="password" data-test="disable-pwd-mgr-2" style="display: none" value="disable-pwd-mgr-2" />
			<input name="disable-pwd-mgr-3" type="password" data-test="disable-pwd-mgr-3" style="display: none" value="disable-pwd-mgr-3" />

			<div class="row">
				<label for="user-password" class="col-4 col-form-label">{{ $t('password') }}</label>
				<div class="col-8 d-flex flex-column">
					<input
						data-test="user-password"
						class="form-control mb-1"
						type="password"
						maxlength="60"
						v-model="user.password"
						:class="{ 'is-invalid': !isValidPassword }"
					/>
					<div class="invalid-feedback">
						{{ $t('user.validPassword') }}
					</div>
				</div>
			</div>

			<div class="row">
				<label for="user-password2" class="col-4 col-form-label">{{ $t('password') }} x2</label>
				<div class="col-8 d-flex flex-column">
					<input
						data-test="user-password2"
						class="form-control mb-1"
						type="password"
						maxlength="60"
						v-model="password2"
						:class="{ 'is-invalid': !isValidPassword2 }"
					/>
					<div class="invalid-feedback">
						{{ $t('user.validPassword2') }}
					</div>
				</div>
			</div>
		</template>

		<div v-if="!canEdit && isValidEmail && !user.active" class="row">
			<label class="col-4 col-form-label">{{ $t('emailActivate') }}</label>
			<div class="col-8 d-flex justify-content-end">
				<button data-test="user-resend-btn" class="btn btn-primary" @click="resendActivation">{{ $t('send') }}</button>
			</div>
		</div>
	</div>

	<ConfirmDeleteDialog
		:responsible="userAsResponsible"
		v-model="showDeleteDialog"
		@confirm="confirmDelete"
		@closed="deleteDialogClosed"
	></ConfirmDeleteDialog>

	<ReplaceResponsibleDialog
		:responsible="userAsResponsible"
		v-model="showRespDialog"
		@confirm="confirmReplace"
		@closed="deleteDialogClosed"
	></ReplaceResponsibleDialog>
</template>
